import { ApplicationRef } from '@angular/core';
import { PerfStackSampler } from './perf-stack-sampler';

const SLOW_MS = 100;
const JANK_MS = 16;
const MAX_ENTRIES = 12;
const REFRESH_MS = 500;

interface PerfEntry {
    readonly at: number;
    readonly duration: number;
    readonly label: string;
}

interface LongAnimationFrameScript {
    readonly sourceFunctionName: string;
    readonly sourceURL: string;
    readonly sourceCharPosition: number;
    readonly invoker: string;
    readonly duration: number;
    readonly forcedStyleAndLayoutDuration: number;
}

interface LongAnimationFrameTiming extends PerformanceEntry {
    readonly scripts: readonly LongAnimationFrameScript[];
}

interface RunningOverlay {
    readonly stop: () => void;
}

let running: RunningOverlay | null = null;

export function setPerfOverlayEnabled(appRef: ApplicationRef, enabled: boolean): void {
    if (enabled && !running) {
        running = startPerfOverlay(appRef);
    } else if (!enabled && running) {
        running.stop();
        running = null;
    }
}

function startPerfOverlay(appRef: ApplicationRef): RunningOverlay {
    const ticks: PerfEntry[] = [];
    const frames: PerfEntry[] = [];
    let frameCount = 0;
    let fps = 0;
    let lastFpsAt = performance.now();

    const originalTick = appRef.tick;

    appRef.tick = () => {
        const start = performance.now();

        try {
            originalTick.call(appRef);
        } finally {
            push(ticks, { at: start, duration: performance.now() - start, label: 'tick' });
        }
    };

    let observer: PerformanceObserver | null = null;
    const sampler = new PerfStackSampler();

    if (PerformanceObserver.supportedEntryTypes.includes('long-animation-frame')) {
        observer = new PerformanceObserver(list => {
            for (const entry of list.getEntries() as LongAnimationFrameTiming[]) {
                const scripts = [...entry.scripts].sort((a, b) => b.duration - a.duration);
                const script = scripts[0];

                push(frames, {
                    at: entry.startTime,
                    duration: entry.duration,
                    label: script ? script.sourceFunctionName || script.invoker || 'script' : 'render',
                });

                if (entry.duration > SLOW_MS) {
                    sampler.report({
                        start: entry.startTime,
                        duration: entry.duration,
                        scripts: scripts.map(item => ({
                            name: item.sourceFunctionName,
                            source: item.sourceURL ? `${item.sourceURL}@${item.sourceCharPosition}` : '',
                            invoker: item.invoker,
                            duration: item.duration,
                            forcedLayout: item.forcedStyleAndLayoutDuration,
                        })),
                    });
                }
            }
        });
        observer.observe({ type: 'long-animation-frame', buffered: false });
    }

    let frameRequest = 0;
    const countFrame = (now: number): void => {
        frameCount++;

        if (now - lastFpsAt >= 1000) {
            fps = Math.round(frameCount * 1000 / (now - lastFpsAt));
            frameCount = 0;
            lastFpsAt = now;
        }

        frameRequest = requestAnimationFrame(countFrame);
    };

    frameRequest = requestAnimationFrame(countFrame);

    const host = document.createElement('div');

    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = [
        'position:fixed', 'left:8px', 'bottom:8px', 'z-index:2147483647', 'width:260px', 'padding:8px',
        'font:11px/1.4 ui-monospace,monospace', 'color:#e5e7eb', 'background:rgb(17 24 39 / 88%)',
        'border-radius:6px', 'pointer-events:none', 'white-space:pre',
    ].join(';');
    document.body.appendChild(host);

    const refresh = setInterval(() => {
        const slowTicks = ticks.filter(tick => tick.duration > SLOW_MS).length;

        host.innerHTML = [
            `<b style="color:${fps < 50 ? '#f87171' : '#4ade80'}">${fps} fps</b>`,
            `\nticks (slow >${SLOW_MS}ms: ${slowTicks})`,
            ...ticks.map(format),
            '\nlong frames',
            ...frames.map(format),
        ].join('\n');
    }, REFRESH_MS);

    return {
        stop: () => {
            appRef.tick = originalTick;
            observer?.disconnect();
            sampler.stop();
            cancelAnimationFrame(frameRequest);
            clearInterval(refresh);
            host.remove();
        },
    };
}

function push(entries: PerfEntry[], entry: PerfEntry): void {
    entries.unshift(entry);
    entries.length = Math.min(entries.length, MAX_ENTRIES);
}

function format(entry: PerfEntry): string {
    const color = entry.duration > SLOW_MS ? '#f87171' : entry.duration > JANK_MS ? '#fbbf24' : '#9ca3af';
    const seconds = (entry.at / 1000).toFixed(1).padStart(7);
    const label = entry.label.replace(/[<>&]/g, '').slice(0, 18);

    return `<span style="color:${color}">${seconds}s ${entry.duration.toFixed(1).padStart(6)}ms ${label}</span>`;
}
